import { useMemo, useState } from "react";
import { ClipboardPaste } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// TSV from Excel; quoted cells may contain tabs/newlines
const parseTsv = (txt) => {
  const rows = [[]];
  let cell = "", q = false;
  for (let i = 0; i < txt.length; i++) {
    const ch = txt[i];
    if (q) {
      if (ch === '"' && txt[i + 1] === '"') { cell += '"'; i++; } else if (ch === '"') q = false; else cell += ch;
    } else if (ch === '"' && cell === "") q = true;
    else if (ch === "\t" || (ch === ";" && !txt.includes("\t"))) { rows[rows.length - 1].push(cell); cell = ""; }
    else if (ch === "\n") { rows[rows.length - 1].push(cell); cell = ""; rows.push([]); }
    else if (ch !== "\r") cell += ch;
  }
  rows[rows.length - 1].push(cell);
  return rows.filter((r) => r.some((c) => c.trim()));
};

export const toNum = (v) => {
  let s = String(v ?? "").replace(/[^\d.,-]/g, "");
  if (!s) return NaN;
  const d = s.lastIndexOf("."), c = s.lastIndexOf(",");
  if (d > -1 && c > -1) s = d > c ? s.replace(/,/g, "") : s.replace(/\./g, "").replace(",", ".");
  else if (c > -1) s = /,\d{1,2}$/.test(s) && (s.match(/,/g) || []).length === 1 ? s.replace(",", ".") : s.replace(/,/g, "");
  else if (d > -1 && ((s.match(/\./g) || []).length > 1 || /\.\d{3}$/.test(s))) s = s.replace(/\./g, "");
  return Number(s);
};

const SRC_OPTS = [["import", "Import"], ["local", "Lokal"], ["support", "Material Pendukung"], ["service", "Service"]];

const isNum = (c) => /^-?[\d.,]+$/.test(c) && !Number.isNaN(toNum(c));

// auto-detect: 1st text = name, 2nd text = detail, 1st number = qty, last number = cost
const parseRow = (r) => {
  const cells = r.map((c) => (c || "").trim());
  const ni = cells.map((c, i) => (c && isNum(c) ? i : -1)).filter((i) => i >= 0);
  if (!ni.length) return null;
  const texts = cells.slice(0, ni[0]).filter(Boolean);
  const after = cells.slice(ni[0] + 1).filter((c) => c && !isNum(c));
  const unit = after.find((c) => c.length <= 8 && !/\s/.test(c)) || "";
  const desc = [...texts.slice(1), ...after.filter((c) => c !== unit)].join("\n");
  return { name: texts[0] || "", qty: toNum(cells[ni[0]]), unit_cost: ni.length > 1 ? toNum(cells[ni[ni.length - 1]]) : 0, unit, description: desc };
};

export const PasteItems = ({ source, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState("");
  const [srcs, setSrcs] = useState({});
  const items = useMemo(() => parseTsv(txt).map(parseRow).filter((r) => r && r.name && !Number.isNaN(r.qty)), [txt]);
  const srcOf = (i) => srcs[i] || source;
  const add = () => { onAdd(items.map((r, i) => ({ ...r, source: srcOf(i), unit: r.unit || "pcs", unit_cost: Number.isNaN(r.unit_cost) ? 0 : r.unit_cost }))); setTxt(""); setSrcs({}); setOpen(false); };
  const setAll = (v) => v && setSrcs(Object.fromEntries(items.map((_, i) => [i, v])));
  return (
    <>
      <Button variant="outline" size="sm" className="mt-2 ml-2 bg-white" onClick={() => setOpen(true)} data-testid={`item-paste-${source}-btn`}><ClipboardPaste className="h-4 w-4 mr-1" />Paste dari Excel</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-heading">Paste Banyak Item dari Excel</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Copy baris langsung dari Excel lalu paste di bawah. Kolom dikenali otomatis: <b>teks pertama = Nama, teks kedua = Product Detail, angka pertama = Qty, angka terakhir = Harga Beli</b>, unit kosong = pcs. Baris judul/tanpa angka dilewati. Cek hasilnya di tabel preview.</p>
          <Textarea rows={6} className="bg-white font-mono text-xs" placeholder={"Kamera IP 4MP\t10\t1.250.000\tpcs\tIR 30m"} value={txt} onChange={(e) => setTxt(e.target.value)} data-testid="paste-items-input" />
          {!!items.length && (
            <>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                Ubah semua ke
                <select className="rounded-md border border-slate-200 bg-white px-2 py-1" value="" onChange={(e) => setAll(e.target.value)} data-testid="paste-set-all-select">
                  <option value="">Pilih bagian…</option>
                  {SRC_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <span className="text-slate-400">Import otomatis harga beli dalam USD</span>
              </div>
              <div className="max-h-56 overflow-auto rounded-lg border border-slate-200">
                <table className="tbl w-full text-xs" data-testid="paste-items-preview">
                  <thead><tr><th>#</th><th>Bagian</th><th>Nama</th><th className="text-right">Qty</th><th className="text-right">Harga Beli</th><th>Unit</th><th>Detail</th></tr></thead>
                  <tbody>{items.map((r, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td><select className="rounded-md border border-slate-200 bg-white px-1 py-0.5" value={srcOf(i)} onChange={(e) => setSrcs((s) => ({ ...s, [i]: e.target.value }))} data-testid={`paste-src-${i}`}>{SRC_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></td>
                      <td>{r.name}</td><td className="text-right font-num">{r.qty.toLocaleString("id-ID")}</td>
                      <td className="text-right font-num">{srcOf(i) === "import" ? "$ " : ""}{Number.isNaN(r.unit_cost) ? "0" : r.unit_cost.toLocaleString("id-ID")}</td>
                      <td>{r.unit || "pcs"}</td><td className="whitespace-pre-line">{r.description}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </>
          )}
          <Button disabled={!items.length} onClick={add} data-testid="paste-items-confirm-btn">Tambahkan {items.length} item</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
